import React, { useState, useEffect, useCallback } from 'react';
import './UGStudentDashboard.css';
import collegeConfig from '../../config/collegeConfig';
import { FaChevronLeft, FaBookReader, FaPlus, FaTimes, FaPencilAlt, FaTrash } from 'react-icons/fa';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';

const emptyForm = () => ({
  topic: '',
  date: new Date().toISOString().slice(0, 10),
  learningObjectives: '',
  resourcesUsed: '',
  summaryReflection: ''
});

const formatDate = d => new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const UGSdl = ({ onBack }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalEntry, setModalEntry] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/sdl/mine`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setEntries(data.entries || []);
    } catch (err) {
      console.error('Error fetching SDL entries:', err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const openNew = () => { setForm(emptyForm()); setModalEntry('new'); };
  const openEdit = (entry) => {
    setForm({
      topic: entry.topic,
      date: new Date(entry.date).toISOString().slice(0, 10),
      learningObjectives: entry.learningObjectives || '',
      resourcesUsed: entry.resourcesUsed || '',
      summaryReflection: entry.summaryReflection || ''
    });
    setModalEntry(entry);
  };

  const submit = async () => {
    if (!form.topic.trim()) {
      alert('Please fill in Topic before saving.');
      return;
    }
    const isEdit = modalEntry && modalEntry !== 'new';
    try {
      setSubmitting(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/sdl${isEdit ? `/${modalEntry._id}` : ''}`, {
        method: isEdit ? 'PUT' : 'POST',
        headers: authHeaders(),
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setModalEntry(null);
        await fetchEntries();
      } else {
        alert(data.message || 'Failed to save SDL entry');
      }
    } catch (err) {
      console.error('Error saving SDL entry:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this SDL entry? This cannot be undone.')) return;
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/sdl/${entry._id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await res.json();
      if (data.success) await fetchEntries();
      else alert(data.message || 'Failed to delete');
    } catch (err) {
      console.error('Error deleting SDL entry:', err);
    }
  };

  return (
    <main className="ug-main">
      <button className="ug-back-btn" onClick={onBack}><FaChevronLeft /> Back to Dashboard</button>
      <h2 className="ug-section-title"><FaBookReader /> Self-Directed Learning</h2>
      <p className="ug-modal-hint" style={{ margin: '0 0 14px 0' }}>
        Record independent learning sessions with objectives, resources, and reflections.
      </p>

      <button className="ug-btn-primary ug-reflection-add-btn" onClick={openNew}>
        <FaPlus /> Add SDL entry
      </button>

      {loading ? (
        <div className="ug-due-empty">Loading SDL entries...</div>
      ) : entries.length === 0 ? (
        <div className="ug-due-empty">
          No SDL entries yet. Start recording your self-directed learning activities.
        </div>
      ) : (
        <>
        <div className="ug-reflection-list">
          {entries.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => (
            <div key={entry._id} className="ug-reflection-card">
              <div className="ug-reflection-card-top">
                <span className="ug-reflection-date">{formatDate(entry.date)}</span>
              </div>
              <h4 className="ug-reflection-period">{entry.topic}</h4>

              {entry.learningObjectives && (
                <>
                  <div className="ug-reflection-section-label">Learning Objectives</div>
                  <p className="ug-reflection-text">{entry.learningObjectives}</p>
                </>
              )}
              {entry.resourcesUsed && (
                <>
                  <div className="ug-reflection-section-label">Resources Used</div>
                  <p className="ug-reflection-text">{entry.resourcesUsed}</p>
                </>
              )}
              {entry.summaryReflection && (
                <>
                  <div className="ug-reflection-section-label">Summary &amp; Reflection</div>
                  <p className="ug-reflection-text">{entry.summaryReflection}</p>
                </>
              )}
              {entry.facultyFeedback && (
                <>
                  <div className="ug-reflection-section-label">Faculty Feedback</div>
                  <p className="ug-reflection-text ug-reflection-faculty-feedback">{entry.facultyFeedback}</p>
                </>
              )}

              <div className="ug-reflection-card-actions">
                <button className="ug-reflection-icon-btn" onClick={() => openEdit(entry)} title="Edit"><FaPencilAlt /></button>
                <button className="ug-reflection-icon-btn ug-reflection-icon-btn-danger" onClick={() => remove(entry)} title="Delete"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
        <Pagination currentPage={currentPage} totalCount={entries.length} onPageChange={setCurrentPage} />
        </>
      )}

      {modalEntry && (
        <div className="ug-modal-overlay" onClick={() => !submitting && setModalEntry(null)}>
          <div className="ug-modal" onClick={e => e.stopPropagation()}>
            <div className="ug-modal-head">
              <h3>{modalEntry === 'new' ? 'Add SDL entry' : 'Edit SDL entry'}</h3>
              <FaTimes onClick={() => !submitting && setModalEntry(null)} />
            </div>

            <label className="ug-form-label">Topic</label>
            <input
              type="text"
              className="ug-form-input"
              placeholder="e.g. Interpreting arterial blood gas reports"
              value={form.topic}
              onChange={e => setForm({ ...form, topic: e.target.value })}
            />

            <label className="ug-form-label">Date</label>
            <input
              type="date"
              className="ug-form-input"
              value={form.date}
              onChange={e => setForm({ ...form, date: e.target.value })}
            />

            <label className="ug-form-label">Learning objectives (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.learningObjectives}
              onChange={e => setForm({ ...form, learningObjectives: e.target.value })}
            />

            <label className="ug-form-label">Resources used (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.resourcesUsed}
              onChange={e => setForm({ ...form, resourcesUsed: e.target.value })}
            />

            <label className="ug-form-label">Summary &amp; reflection (optional)</label>
            <textarea
              className="ug-form-input ug-form-textarea"
              value={form.summaryReflection}
              onChange={e => setForm({ ...form, summaryReflection: e.target.value })}
            />

            <div className="ug-modal-actions">
              <button className="ug-btn-secondary" onClick={() => setModalEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ug-btn-primary" onClick={submit} disabled={submitting}>
                {submitting ? 'Saving...' : 'Save entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UGSdl;
